/*
 *   File : portfolio-analysis.js
 *   Author : https://evoqins.com
 *   Description : portfolio analysis page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

// Components
import { APIService } from '../../Service';
import { Footer } from '../../Component/Footer';
import { BreadCrumbs, EmptyScreen } from '../../Component/Other';
import { Icon } from '../../Component/Icon';
import { SectionTitle } from '../../Component/Title';
import OverLay from "../../Component/Other/overlay";

// Assets
import Loader from "../../Assets/Images/coloured_loader.svg";
import { useLocation, useNavigate } from 'react-router-dom';
import { FundsDetailsModal, PortfolioFilterModal } from '../../Component/Modal';
import { AreaChart } from '../../Component/Chart';
import { FilterCard } from '../../Component/Cards';

// Custom Data
const BREADCRUMBS = [
    {
        label: "Portfolio",
        path: "/portfolio"
    },
    {
        label: "Portfolio Analysis",
        path: ""
    },
];

const DEFAULT_FILTER_INVESTMENT_TYPE = {
    id: null,
    label: "All"
};

const DEFAULT_FILTER_PORTFOLIO_FOR = {
    id: -1,
    label: "My portfolio"
};

const FILTER_PORTFOLIO_FOR = [
    {
        id: null,
        label: "All"
    },
    {
        id: -1,
        label: "My portfolio"
    },
];

const FILTERS = [
    {
        "period": "1Y",
        "filter_type": 4
    },
    {
        "period": "3Y",
        "filter_type": 5
    },
    {
        "period": "Since inception",
        "filter_type": 7
    }
];


const fund = {
    icon: "https://index-funds-dev-static-assets.s3.ap-south-1.amazonaws.com/amc_images/icici_groww.png",
    name: "ICICI Prudential Nifty 50 Index Direct Plan Growth t Growth",
    active_sip: true,
    current_amount_text: "₹2,468",
    invested_amount_text: "₹2,468"
}

const ASSET_ALLOCATION_DATA = [
    {
        label: "Equity",
        value: 50,
        color: "#223588",
        funds: [fund, fund, fund]
    },
    {
        label: "Debt",
        value: 30,
        color: "#5676EF",
        funds: [fund, fund, fund]
    },
    {
        label: "Gold/Silver",
        value: 10,
        color: "#F6C44B",
        funds: [fund, fund, fund]
    },
    {
        label: "Others",
        value: 10,
        color: "#ECEEF5",
        funds: [fund, fund, fund]
    }
];

const MARKET_CAP_BREAKDOWN_DATA = [
    {
        name: "Large Cap",
        percentage: 100,
        color: "#4739CC",
        funds: [fund, fund, fund]
    },
    {
        name: "Mid",
        percentage: 0,
        color: "#A5B4FC",
        funds: [fund, fund, fund]
    },
    {
        name: "Small",
        percentage: 0,
        color: "#F8B665",
        funds: [fund, fund, fund]
    },
    {
        name: "Multi Cap",
        percentage: 0,
        color: "#ECEEF5",
        funds: [fund, fund, fund]
    }
];






const PortfolioAnalysis = () => {

    const initialRender = useRef(true);
    const chartRef = useRef("");
    const navigate = useNavigate();
    const location = useLocation();

    const filterOptionsPortfolioFor = location.state !== null ? location.state.filter_options_portfolio_for : FILTER_PORTFOLIO_FOR;
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [portfolioSummary, setShowPortfolioSummary] = useState("");
    const [toggleFilterModal, setToggleFilterModal] = useState(false);
    const [filterPortfolioFor, setFilterPortfolioFor] = useState(location.state !== null ? location.state.selected_portfolio_for_filter : DEFAULT_FILTER_PORTFOLIO_FOR);
    const [filterInvestmentType, setFilterInvestmentType] = useState(location.state !== null ? location.state.selected_investment_type_filter : DEFAULT_FILTER_INVESTMENT_TYPE);

    const [performanceData, setPerformanceData] = useState({});
    const [chartFilters, setChartFilters] = useState(FILTERS);
    const [filterType, setFilterType] = useState("");
    const [showFundDetails, setShowFundDetails] = useState(false);
    const [funds, setFunds] = useState([]);
    const [chartLoading, setChartLoading] = useState(false);


    useEffect(() => {
        setIsDataLoading(true);
        _getPortfolioAnalyses();

        window.scrollTo({
            top: 0, behavior: "smooth"
        });

        window.addEventListener("click", (e) => {
            if (!e.target.closest("#portfolio-filter")) {
                setToggleFilterModal(false);
            }
        });

    }, []);

    useEffect(() => {
        if (showFundDetails) {
            new Modal(document.getElementById('fund-details-modal')).show();
        }
    }, [showFundDetails]);

    useEffect(() => {
        if (toggleFilterModal === true) {
            document.body.style.overflow = "hidden";
            document.querySelector("#portfolio-filter").scrollIntoView({ top: 0, behavior: "smooth" });
        } else {
            document.body.style.overflow = "unset";
        };

        // Removing backdrop form UI when user goes back in history while filter modals opened
        window.addEventListener('popstate', function () {
            setToggleFilterModal(false);
        });

    }, [toggleFilterModal]);

    useEffect(() => {
        if (initialRender.current === false) {
            _getPortfolioAnalyses();
            // Saving selected filters in location state
            navigate("/portfolio/analysis", {
                state: {
                    "selected_portfolio_for_filter": filterPortfolioFor,
                    "selected_investment_type_filter": filterInvestmentType,
                    "filter_options_portfolio_for": filterOptionsPortfolioFor
                },
                replace: true
            });
        }

    }, [filterPortfolioFor, filterInvestmentType]);


    // Setting filters when user reloads the page
    useEffect(() => {
        window.addEventListener("load", () => {
            setFilterPortfolioFor(location.state !== null ? location.state.selected_portfolio_for_filter : DEFAULT_FILTER_PORTFOLIO_FOR)
            setFilterInvestmentType(location.state !== null ? location.state.selected_investment_type_filter : DEFAULT_FILTER_INVESTMENT_TYPE)
        });
    }, []);


    const _handlePortfolioFilterModal = () => {
        setToggleFilterModal(status => status == false ? true : false);
    };

    const _handleCloseFilterModal = () => {
        setToggleFilterModal(false);
    };

    const _handleApplyFilters = (selected_portfolio_for, selected_investment_type, status) => {
        if (status == true) {
            setFilterPortfolioFor({ ...selected_portfolio_for });
            setFilterInvestmentType({ ...selected_investment_type });
            setIsDataLoading(true);
        }
        setToggleFilterModal(false);
    };


    const _handleFilterTypeChange = (type) => {
        setChartLoading(true);

        setTimeout(() => {
            setFilterType(type);

            const performance_chart = document.getElementById("performance-chart-wrapper");
            performance_chart.scrollIntoView(
                {
                    behavior: "smooth",
                    inline: "start",
                    block: "nearest",
                }
            );
            setChartLoading(false);
        }, 300);

    };

    const _handleClickAllocationItem = ({ funds }) => {
        if (funds?.length !== 0) {
            setShowFundDetails(true);
            setFunds(funds);
        }
    };

    const _handleCloseFundDetailsModal = () => {
        setShowFundDetails(false);
    }


    // API - Get portfolio analyses data
    const _getPortfolioAnalyses = () => {
        let api_version = 2;
        let is_post = true;
        let url = 'dashboard/portfolio/analysis';
        let data = {
            "investment_type": filterInvestmentType?.id,
            "family_detail_id": filterPortfolioFor?.id,
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                setShowPortfolioSummary(response.data);
                let chart_filters = Object.keys(response.data?.graph?.data || {});
                chart_filters = chart_filters.filter(filter =>
                    Array.isArray(response.data?.graph?.data?.[filter]) &&
                    response.data.graph.data[filter].length > 0
                );
                setChartFilters(chart_filters);
                setFilterType(chart_filters.at(-1));

                let chart_data = response.data?.graph?.data;
                let updated_chart_data = {};

                if (chart_data && chart_filters.length > 0) {
                    chart_filters.forEach(filter_type => {
                        const series = chart_data[filter_type] || [];
                        updated_chart_data[filter_type] = {
                            labels: series.map(item => item.date) || null,
                            datasets: series.map(item => item.value) || null
                        };
                    });
                }
                setPerformanceData(updated_chart_data);
            } else {
            };

            setIsDataLoading(false);
            initialRender.current = false;
        });
    }

    // *********** Inner Components ***************//
    const BreakDownDataCard = ({ breakdownData, title, handleClick }) => {
        return (
            <div className="e-bg-cultured padding-32px-all padding-16px-all-res  e-border-radius-16 h-100">
                <SectionTitle title={title}
                    fontFamily="e-primary-font-inter"
                    fontSiz="e-font-20"
                    className=" e-text-eerie-black mb-sm-4 mb-3" />

                {
                    breakdownData.length === 0 ?
                        <div className="mt-5">
                            <EmptyScreen img={require("../../Assets/Images/EmptyScreen/no-data.webp")}
                                title="No data found"
                                type={2}
                                description="There is nothing to show right now." />
                        </div>
                        :
                        <>
                            <div className="mb-sm-4 mb-3">
                                <div className="progress h-16 e-bg-cultured e-border-radius-4">
                                    {
                                        (breakdownData || []).map((item, id) => (
                                            <div
                                                key={id}
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{
                                                    width: `${item.percentage}%`,
                                                    backgroundColor: item.color,
                                                }}>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            <div className='d-flex flex-column row-gap-sm-4 row-gap-2'>
                                {
                                    (breakdownData || []).map((item, id) => (
                                        <div className="d-flex align-items-center justify-content-between pt-sm-3 padding-12px-tb-res padding-18px-bottom border-bottom border-color-gray-hint hover-arrow-wrapper cursor-pointer"
                                            key={id}
                                            onClick={() => handleClick(item)}>
                                            <div className="d-flex  gap-2 align-items-center">
                                                <span
                                                    className="d-inline-block w-16px h-16 e-border-radius-4"
                                                    style={{ backgroundColor: item.color }}>
                                                </span>
                                                <span className="e-color-black e-font-14-res e-font-16 e-primary-font-inter e-font-weight-400 line-height-28px">{item.name}</span>
                                            </div>
                                            <div className="d-flex gap-2 align-items-center">
                                                <span className="e-color-black e-font-14-res e-font-16 e-primary-font-inter e-font-weight-400 line-height-28px">{item.percentage_text}</span>
                                                <Icon icon="arrow-right-allocation"
                                                    size={16}
                                                    className={`${item?.funds?.length == 0 ? "invisible" : ""} hover-arrow`} />
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </>
                }
            </div>
        )
    };


    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 align-items-start e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">
                <div className="col-lg-11 p-0">



                    <div className="row  row-gap-3 flex-column flex-sm-row justify-content-sm-between align-items-sm-center">
                        <div className="col-auto">

                            <BreadCrumbs data={BREADCRUMBS} />
                        </div>
                        <div className="col-auto ms-auto">
                            <div className={`position-relative`} id="portfolio-filter">
                                <FilterCard filterText={`${filterPortfolioFor?.label} ${filterInvestmentType.id && filterInvestmentType.id !== null ? ", +1" : ""}`}
                                    handleOpenFilterModal={_handlePortfolioFilterModal} />

                                {/* filter modal */}
                                {
                                    toggleFilterModal &&
                                    <>
                                        <OverLay isOpen={toggleFilterModal} onClose={_handleCloseFilterModal} />
                                        <PortfolioFilterModal
                                            filterOptionsPortfolioFor={filterOptionsPortfolioFor}
                                            selectedFilterPortfolioFor={filterPortfolioFor}
                                            selectedFilterInvestmentType={filterInvestmentType}
                                            onClick={_handleCloseFilterModal}
                                            handleSelectedFilters={_handleApplyFilters} />
                                    </>
                                }
                            </div>
                        </div>
                    </div>

                    {
                        isDataLoading ? (
                            <div className="min-h-100vh text-center mt-5" >
                                <img src={Loader}
                                    draggable="false"
                                    alt="loader" />
                            </div>
                        ) : (
                            <>
                                <div className="row mt-4 margin-32px-bottom">
                                    <div className="col-12">
                                        <div className="e-bg-cultured py-3 py-sm-4  e-padding-16px-lr-res padding-32px-lr e-border-radius-16">

                                            <div className="d-flex justify-content-between mb-4">
                                                <div>
                                                    <p className="e-text-dark-heather  e-font-14-res e-font-16 e-primary-font-inter e-font-weight-400 line-height-22px mb-0">
                                                        Current
                                                    </p>
                                                    <span className="e-text-eerie_black e-font-14-res e-font-16 e-primary-font-inter e-font-weight-600 line-height-22px">
                                                        {portfolioSummary?.summary?.portfolio_value_text || "₹0"}
                                                    </span>
                                                </div>
                                                <div className='text-end'>
                                                    <p className="e-text-dark-heather  e-font-14-res e-font-16 e-primary-font-inter e-font-weight-400 line-height-22px mb-0">
                                                        Invested
                                                    </p>
                                                    <span className="e-text-eerie_black e-font-14-res e-font-16 e-primary-font-inter e-font-weight-600 line-height-22px">
                                                        {portfolioSummary?.summary.invested_value_text || "₹0"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="e-over-flow-auto h-100">
                                                <div id="performance-chart-wrapper"
                                                    className="h-100 d-flex flex-column e-bg-cultured e-border-radius-16  position-relative e-graph-wrapper" >

                                                    {
                                                        performanceData[filterType] ?
                                                            <>
                                                                <div id="fund-chart"
                                                                    ref={chartRef}
                                                                    className="flex-grow-1 h-240">
                                                                    {
                                                                        chartLoading ? (
                                                                            <div style={{ height: chartRef.current.clientHeight }}
                                                                                className="text-center pt-lg-5">
                                                                                <img src={Loader}
                                                                                    draggable="false" />
                                                                            </div>
                                                                        ) : (
                                                                            <AreaChart data={performanceData[filterType]}
                                                                                tenure={filterType.filter_type}
                                                                                type={2} />
                                                                        )
                                                                    }
                                                                </div>
                                                                {/* filters */}
                                                                <div className="align-self-center d-flex gap-3 gap-sm-4 align-items-center flex-wrap bg-white e-border-radius-16 px-2 px-sm-3 py-2" >
                                                                    {
                                                                        chartFilters.map((item, key) => (
                                                                            <p className={`${filterType === item ? "e-text-egyptian-blue e-bg-lavender e-font-weight-600  e-border-radius-8"
                                                                                : "e-text-chinese_black e-font-weight-400 "} e-alt-font-poppins e-font-12-res e-font-14 cursor-pointer padding-6-lr py-1 mb-0`}
                                                                                key={key}
                                                                                onClick={() => _handleFilterTypeChange(item)}>
                                                                                {item}
                                                                            </p>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </>
                                                            :
                                                            <div className="p-5 my-5 text-center">
                                                                <p className="py-5 e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-500 mb-0" >
                                                                    Fund Performance data not available!!
                                                                </p>
                                                            </div>

                                                    }
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                <div className="row gx-32px row-gap-4">
                                    <div className="col-12 col-sm-6">
                                        <BreakDownDataCard title={"Category distribution"}
                                            breakdownData={portfolioSummary.category_distribution || []}
                                            handleClick={_handleClickAllocationItem} />
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <BreakDownDataCard title={"Market-cap breakdown"}
                                            breakdownData={portfolioSummary.market_cap_breakdown || []}
                                            handleClick={_handleClickAllocationItem} />
                                    </div>
                                </div>
                            </>
                        )
                    }
                </div>
            </div>
            <Footer />

            {
                showFundDetails &&
                <FundsDetailsModal funds={funds} onClose={_handleCloseFundDetailsModal} />
            }
        </>
    );
};

export default PortfolioAnalysis;